import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronLeft } from 'lucide-react';
import { createPortal } from 'react-dom';

const formatTimeStr = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const isPM = h >= 12;
  const displayH = h % 12 || 12;
  return `${displayH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;
};

export const CircularTimePicker = ({ value, onChange, label, className = "w-full" }) => {
  const [display, setDisplay] = useState(formatTimeStr(value));
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('hours'); // 'hours' or 'minutes'
  
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setDisplay(formatTimeStr(value));
  }, [value]);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      setMode('hours'); // Reset to hours view on open
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        zIndex: 999999
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleBlur = () => {
    let str = display.toLowerCase().trim();
    if (!str) return;
    let isPM = str.includes('p');
    let isAM = str.includes('a');
    let nums = str.replace(/[^0-9:]/g, '');
    let parts = nums.split(':');
    
    let h = parseInt(parts[0] || '0', 10);
    let m = parseInt(parts[1] || '0', 10);

    if (parts.length === 1 && nums.length >= 3 && !nums.includes(':')) {
      h = parseInt(nums.substring(0, nums.length - 2), 10);
      m = parseInt(nums.substring(nums.length - 2), 10);
    }
    
    if (isPM && h < 12) h += 12;
    if (isAM && h === 12) h = 0;
    
    if (h < 0 || h > 23 || isNaN(h)) h = 0;
    if (m < 0 || m > 59 || isNaN(m)) m = 0;

    const formatted24 = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    onChange(formatted24);
    setDisplay(formatTimeStr(formatted24));
  };

  let [valH, valM] = (value || '08:00').split(':').map(Number);
  let isPM = valH >= 12;
  let displayH = valH % 12 || 12;

  const setTime = (newH, newM, newPM) => {
    let finalH = newH;
    if (newPM && finalH < 12) finalH += 12;
    if (!newPM && finalH === 12) finalH = 0;
    const formatted24 = `${finalH.toString().padStart(2, '0')}:${newM.toString().padStart(2, '0')}`;
    onChange(formatted24);
  };

  const clockFaceRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleClockInteraction = (e) => {
    if (!clockFaceRef.current) return;
    const rect = clockFaceRef.current.getBoundingClientRect();
    const centerOffset = 80; // center is at 80,80
    const x = e.clientX - rect.left - centerOffset;
    const y = e.clientY - rect.top - centerOffset;
    
    let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;
    
    if (mode === 'hours') {
      let h = Math.round(angle / 30);
      if (h === 0) h = 12;
      if (h === 12 && angle > 345) h = 12; // edge case near 0/360
      setTime(h, valM, isPM);
    } else {
      let m = Math.round(angle / 6);
      if (m === 60) m = 0;
      setTime(displayH, m, isPM);
    }
  };

  const handleMouseUp = () => {
    if (isDragging && mode === 'hours') {
      setMode('minutes');
    }
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleClockInteraction);
    } else {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleClockInteraction);
    }
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleClockInteraction);
    };
  }, [isDragging, mode, displayH, valM, isPM]);

  const renderClockFace = () => {
    const numbers = mode === 'hours' ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    const radius = 62;
    const center = 80; // 160x160 box

    const currentVal = mode === 'hours' ? displayH : valM;
    
    let angle = 0;
    if (mode === 'hours') {
       angle = (displayH % 12) * 30;
    } else {
       angle = (valM / 60) * 360;
    }
    const handAngle = angle - 90;
    
    // Hand should point exactly to the same radius as the text
    const handX = center + radius * Math.cos(handAngle * Math.PI / 180);
    const handY = center + radius * Math.sin(handAngle * Math.PI / 180);

    return (
      <div 
        ref={clockFaceRef}
        onMouseDown={(e) => {
          setIsDragging(true);
          handleClockInteraction(e);
        }}
        onClick={(e) => {
          handleClockInteraction(e);
          if (mode === 'hours') setMode('minutes');
        }}
        className="relative w-[160px] h-[160px] bg-slate-100 rounded-full mx-auto my-3 flex-shrink-0 cursor-pointer touch-none select-none"
      >
        <div className="absolute w-1.5 h-1.5 bg-[var(--theme-primary)] rounded-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10" />
        
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
          <line x1={center} y1={center} x2={handX} y2={handY} stroke="var(--theme-primary)" strokeWidth="2" className={`transition-all ${isDragging ? 'duration-75' : 'duration-300'} ease-out`} />
          <circle cx={handX} cy={handY} r="12" fill="var(--theme-primary)" className={`transition-all ${isDragging ? 'duration-75' : 'duration-300'} ease-out`} />
        </svg>

        {numbers.map((num, i) => {
          const numAngle = (i * 30) - 90;
          const x = center + radius * Math.cos(numAngle * Math.PI / 180);
          const y = center + radius * Math.sin(numAngle * Math.PI / 180);
          
          // For minutes, only highlight if it exactly matches the major increment
          const isSelected = mode === 'hours' ? currentVal === num : (currentVal % 5 === 0 && currentVal === num);
          const displayNum = mode === 'minutes' ? num.toString().padStart(2, '0') : num;
          
          return (
            <div
              key={num}
              className={`absolute w-6 h-6 -ml-3 -mt-3 rounded-full flex items-center justify-center text-[10px] font-bold z-10 pointer-events-none transition-colors select-none ${isSelected ? 'text-white' : 'text-slate-600'}`}
              style={{ left: `${x}px`, top: `${y}px` }}
            >
              {displayNum}
            </div>
          )
        })}
      </div>
    );
  };

  return (
    <div className={`flex flex-col w-full ${className}`} ref={containerRef}>
      {label && <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">{label}</label>}
      
      <div className="relative">
        <input
          type="text"
          value={display}
          onChange={e => setDisplay(e.target.value)}
          onBlur={handleBlur}
          onClick={() => setIsOpen(true)}
          placeholder="09:00 AM"
          className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] hover:border-[var(--theme-primary)] focus:bg-white rounded-md px-3 py-1.5 pr-8 text-xs font-bold text-[#0d0d0d] outline-none transition-colors"
        />
        <button 
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-all active:scale-95 ${isOpen ? 'bg-[var(--theme-primary)] text-white shadow-sm' : 'text-[var(--theme-primary)] hover:bg-slate-100'}`}
        >
          <Clock size={14} strokeWidth={2.5} />
        </button>
      </div>

      {isOpen && createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-white border border-[var(--theme-tertiary)] rounded-xl shadow-2xl p-3 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-200 w-[230px] z-[99999]" onClick={(e) => e.stopPropagation()}>
          
          {/* Header Controls */}
          <div className="flex items-center justify-between px-1 pt-0.5 pb-2 border-b border-slate-100">
            <div className="flex gap-1.5 items-center">
              <button 
                onClick={() => setMode('hours')} 
                className={`text-2xl font-bold transition-colors ${mode === 'hours' ? 'text-[var(--theme-primary)]' : 'text-slate-300 hover:text-slate-400'}`}
              >
                {displayH.toString().padStart(2, '0')}
              </button>
              <span className="text-2xl font-bold text-slate-300 -mt-1">:</span>
              <button 
                onClick={() => setMode('minutes')} 
                className={`text-2xl font-bold transition-colors ${mode === 'minutes' ? 'text-[var(--theme-primary)]' : 'text-slate-300 hover:text-slate-400'}`}
              >
                {valM.toString().padStart(2, '0')}
              </button>
            </div>
            
            <div className="flex items-center gap-2.5">
              <label className="flex items-center gap-1 cursor-pointer group">
                <input 
                  type="radio" 
                  checked={!isPM} 
                  onChange={() => setTime(displayH, valM, false)}
                  className="w-3.5 h-3.5 cursor-pointer m-0"
                  style={{ accentColor: 'var(--theme-primary)' }}
                />
                <span className={`text-[10px] font-bold transition-colors ${!isPM ? 'text-[var(--theme-primary)]' : 'text-slate-500 group-hover:text-slate-700'}`}>AM</span>
              </label>
              
              <label className="flex items-center gap-1 cursor-pointer group">
                <input 
                  type="radio" 
                  checked={isPM} 
                  onChange={() => setTime(displayH, valM, true)}
                  className="w-3.5 h-3.5 cursor-pointer m-0"
                  style={{ accentColor: 'var(--theme-primary)' }}
                />
                <span className={`text-[10px] font-bold transition-colors ${isPM ? 'text-[var(--theme-primary)]' : 'text-slate-500 group-hover:text-slate-700'}`}>PM</span>
              </label>
            </div>
          </div>

          {/* Clock Face */}
          {renderClockFace()}

          {/* Footer Controls */}
          <div className="flex justify-end pt-1.5 border-t border-slate-100">
            <button onClick={() => setIsOpen(false)} className="px-3 py-1.5 text-[10px] font-extrabold tracking-widest uppercase text-[var(--theme-primary)] hover:bg-[var(--theme-secondary)] rounded-md transition-colors">
              OK
            </button>
          </div>

        </div>,
        document.body
      )}
    </div>
  );
};
